import React, { FC, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import { ReactComponent as TodoSpinnerIcon } from '../../../icons/todo-spinner.svg';

interface TaskDiscussionPlaceholderProps {
  subThreadId: string;
  agentId?: string;
  agentNickname?: string;
  agentImageUrl?: string;
  onDiscussionCreated: (threadId: string) => void;
}

const TaskDiscussionPlaceholder: FC<TaskDiscussionPlaceholderProps> = ({
  subThreadId,
  agentNickname,
  agentImageUrl,
  onDiscussionCreated,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [creating, setCreating] = useState(false);

  const handleStartChatting = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        `/agents/threads/${subThreadId}/create-discussion`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success && response.data.threadId) {
        onDiscussionCreated(response.data.threadId);
      }
    } catch (error) {
      console.error('Error creating discussion thread:', error);
    } finally {
      setCreating(false);
    }
  }, [creating, subThreadId, getAccessTokenSilently, onDiscussionCreated]);

  const nameLabel = agentNickname || 'your assistant';

  return (
    <div className='task-discussion-placeholder'>
      <div className='task-discussion-placeholder-content'>
        {agentImageUrl && (
          <div className='task-discussion-placeholder-avatar'>
            <img src={agentImageUrl} alt={agentNickname || 'Assistant'} />
          </div>
        )}
        <h2 className='task-discussion-placeholder-heading'>
          Chat to {nameLabel}
        </h2>
        <p className='task-discussion-placeholder-subheading'>
          You can chat to me to find out the status of this task, make updates,
          or manage your research.
        </p>
      </div>
      <button
        type='button'
        className='task-discussion-placeholder-cta'
        onClick={handleStartChatting}
        disabled={creating}
      >
        {creating ? (
          <TodoSpinnerIcon className='task-discussion-placeholder-spinner' />
        ) : (
          'Start chatting'
        )}
      </button>
    </div>
  );
};

export default TaskDiscussionPlaceholder;
